import { API, API_BASE, toFullUrl } from '../config/api';
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { FaSpotify, FaApple, FaSoundcloud, FaYoutube } from 'react-icons/fa';
import { Play, Pause, Download, Share2, FileText, Tag, Loader2, ArrowLeft, ShoppingCart, ListPlus } from 'lucide-react';
import { usePlayer } from '../context/PlayerContext';
import { useCart } from '../context/CartContext';
import { EmberBackground } from '../components/EmberBackground';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import CommentSection from '../components/CommentSection';
import LikeButton from '../components/LikeButton';
import { TrackReviews } from '../components/TrackReviews';
import { ShareButtons } from '../components/ShareButtons';
import FireButton from '../components/FireButton';


const STREAMING_ICONS = {
  spotify: FaSpotify,
  apple: FaApple,
  soundcloud: FaSoundcloud,
  youtube: FaYoutube
};

export default function Tracks() {
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const { currentTrack, isPlaying, playTrack, addToQueue } = usePlayer();
  const { addItem } = useCart();

  const [purchaseDialogOpen, setPurchaseDialogOpen] = useState(false);
  const [purchaseTrack, setPurchaseTrack] = useState(null);
  const [discountCode, setDiscountCode] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [validatingDiscount, setValidatingDiscount] = useState(false);
  const [discountInfo, setDiscountInfo] = useState(null);
  const [purchasing, setPurchasing] = useState(false);
  const [lyricsTrack, setLyricsTrack] = useState(null);

  useEffect(() => {
    fetchTracks();
  }, []);

  const fetchTracks = async () => {
    try {
      const res = await axios.get(`${API}/tracks`);
      setTracks(res.data.filter(t => !t.is_upcoming));
    } catch (e) {
      console.error('Failed to fetch tracks', e);
    } finally {
      setLoading(false);
    }
  };

  const handleShare = (track) => {
    if (navigator.share) {
      navigator.share({ title: track.title, text: `Check out "${track.title}" by ${track.artist}`, url: window.location.href });
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied to clipboard!');
    }
  };

  const openPurchaseDialog = (track) => {
    setPurchaseTrack(track);
    setDiscountCode('');
    setCustomerEmail('');
    setDiscountInfo(null);
    setPurchaseDialogOpen(true);
  };

  const validateDiscount = async () => {
    if (!discountCode || !purchaseTrack) return;
    setValidatingDiscount(true);
    try {
      const { data } = await axios.post(`${API}/discounts/validate`, null, { params: { code: discountCode, track_id: purchaseTrack.id } });
      setDiscountInfo(data);
      toast.success(`Discount applied: -$${data.discount_amount.toFixed(2)}`);
    } catch (e) {
      setDiscountInfo(null);
      toast.error(e.response?.data?.detail || 'Invalid discount code');
    } finally {
      setValidatingDiscount(false);
    }
  };

  const handleBuy = async () => {
    if (!purchaseTrack) return;
    setPurchasing(true);
    try {
      const { data } = await axios.post(`${API}/checkout`, {
        track_id: purchaseTrack.id,
        origin_url: window.location.origin,
        discount_code: discountCode || null,
        email: customerEmail || null
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start checkout');
      setPurchasing(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] relative" data-testid="tracks-page">
      <div className="fixed inset-0 z-0 pointer-events-none">
        <EmberBackground />
      </div>

      <div className="relative z-10">
        {/* Header */}
        <div className="border-b border-[#262626] bg-[#050505]/80 backdrop-blur-sm sticky top-0 z-30">
          <div className="max-w-7xl mx-auto px-4 sm:px-8 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3 text-[#A3A3A3] hover:text-white transition-colors" data-testid="back-to-home">
              <ArrowLeft className="w-5 h-5" />
              <span className="font-cabinet font-bold uppercase tracking-wider text-sm">Back</span>
            </Link>
            <h1 className="font-cabinet text-xl sm:text-2xl font-black uppercase tracking-tighter" style={{ fontFamily: "'Orbitron', sans-serif" }}>
              <span style={{ background: 'linear-gradient(90deg, #FF3B30, #DAA520)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                ALL TRACKS
              </span>
            </h1>
            <div className="w-16" />
          </div>
        </div>

        {/* Tracks Grid */}
        <section className="py-12 sm:py-16 px-4 sm:px-8" data-testid="tracks-grid">
          <div className="max-w-7xl mx-auto">
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3].map(i => (
                  <div key={i} className="bg-[#141414] border border-[#262626] p-6 animate-pulse">
                    <div className="aspect-square bg-[#262626] mb-4" />
                    <div className="h-6 bg-[#262626] mb-2" />
                    <div className="h-4 bg-[#262626] w-2/3" />
                  </div>
                ))}
              </div>
            ) : tracks.length === 0 ? (
              <div className="text-center py-20 bg-[#141414] border border-[#262626]">
                <p className="text-[#A3A3A3] text-lg">No tracks available yet.</p>
                <p className="text-[#A3A3A3] mt-2">Check back soon!</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {tracks.map(track => (
                  <div
                    key={track.id}
                    className="link-card p-0 overflow-hidden group"
                    data-testid={`track-card-${track.id}`}
                  >
                    <div className="relative aspect-square bg-[#262626] overflow-hidden">
                      {track.cover_image ? (
                        <img src={toFullUrl(track.cover_image)} alt={track.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <span className="font-cabinet text-4xl font-black text-[#A3A3A3]">{track.title.charAt(0)}</span>
                        </div>
                      )}
                      {track.on_sale && (
                        <div className="absolute top-3 left-3 z-10 bg-[#DAA520] px-3 py-1" data-testid={`sale-badge-${track.id}`}>
                          <span className="text-xs font-bold uppercase text-black tracking-wider">Sale</span>
                        </div>
                      )}
                      {track.preview_url && (
                        <button
                          onClick={() => playTrack(track)}
                          className={`absolute inset-0 flex items-center justify-center bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity ${currentTrack?.id === track.id && isPlaying ? 'opacity-100' : ''}`}
                          data-testid={`play-track-${track.id}`}
                        >
                          <div className={`w-16 h-16 bg-[#FF3B30] flex items-center justify-center ${currentTrack?.id === track.id && isPlaying ? 'playing-indicator' : ''}`}>
                            {currentTrack?.id === track.id && isPlaying ? <Pause className="w-8 h-8 text-white" /> : <Play className="w-8 h-8 text-white ml-1" />}
                          </div>
                        </button>
                      )}
                    </div>
                    <div className="p-4">
                      <h3 className="font-cabinet text-xl font-bold uppercase tracking-tight mb-1">{track.title}</h3>
                      <p className="text-[#A3A3A3] text-sm mb-4">{track.artist}</p>
                      {track.streaming_links && Object.keys(track.streaming_links).length > 0 && (
                        <div className="flex gap-3 mb-4">
                          {Object.entries(track.streaming_links).map(([platform, url]) => {
                            const Icon = STREAMING_ICONS[platform.toLowerCase()];
                            return Icon ? (
                              <a key={platform} href={url} target="_blank" rel="noopener noreferrer"
                                className="w-10 h-10 bg-[#262626] flex items-center justify-center text-[#A3A3A3] hover:text-white hover:bg-[#FF3B30] transition-colors"
                                data-testid={`streaming-${platform}-${track.id}`}>
                                <Icon className="w-5 h-5" />
                              </a>
                            ) : null;
                          })}
                        </div>
                      )}
                      {/* Sale Badge + Price Display */}
                      {track.purchasable && track.price > 0 && (
                        <div className="flex items-center gap-2 mb-3">
                          {track.on_sale && track.sale_price != null ? (
                            <>
                              <span className="px-2 py-0.5 bg-[#DAA520] text-black text-xs font-bold uppercase">Sale</span>
                              <span className="font-mono text-sm text-[#A3A3A3] line-through">${track.price.toFixed(2)}</span>
                              <span className="font-mono text-lg font-bold text-[#DAA520]">${track.sale_price.toFixed(2)}</span>
                            </>
                          ) : (
                            <span className="font-mono text-lg font-bold text-[#FF3B30]">${track.price.toFixed(2)}</span>
                          )}
                        </div>
                      )}
                      <div className="flex gap-2">
                        {track.preview_url && (
                          <Button onClick={() => playTrack(track)} variant="outline" className="flex-1 rounded-none border-[#262626] hover:border-[#FF3B30] hover:text-[#FF3B30]" data-testid={`stream-track-${track.id}`}>
                            {currentTrack?.id === track.id && isPlaying ? <><Pause className="w-4 h-4 mr-2" /> PLAYING</> : <><Play className="w-4 h-4 mr-2" /> STREAM</>}
                          </Button>
                        )}
                        {track.preview_url && (
                          <button onClick={() => { const ok = addToQueue(track); toast[ok ? 'success' : 'message'](ok ? `Added "${track.title}" to queue` : 'Already in queue'); }}
                            className="h-10 w-10 flex items-center justify-center bg-[#141414] hover:bg-[#DAA520] hover:text-black border border-[#262626] transition-colors"
                            title="Add to queue" data-testid={`queue-track-${track.id}`}>
                            <ListPlus className="w-4 h-4" />
                          </button>
                        )}
                        {track.purchasable && track.price > 0 && (
                          <>
                            <button onClick={() => addItem({ item_type: 'track', item_id: track.id, title: track.title, price: track.on_sale && track.sale_price != null ? track.sale_price : track.price, cover_image: track.cover_image ? toFullUrl(track.cover_image) : null })}
                              className="h-10 w-10 flex items-center justify-center bg-[#262626] hover:bg-[#DAA520] hover:text-black transition-colors rounded-none border border-[#262626]"
                              data-testid={`cart-track-${track.id}`}>
                              <ShoppingCart className="w-4 h-4" />
                            </button>
                            <Button onClick={() => openPurchaseDialog(track)} className="flex-1 rounded-none font-bold text-sm" style={{ background: 'var(--primary)', boxShadow: '0 2px 12px rgba(var(--primary-rgb), 0.3)' }} data-testid={`buy-track-${track.id}`}>
                              <Download className="w-4 h-4 mr-2" />
                              {track.on_sale && track.sale_price != null ? `$${track.sale_price.toFixed(2)}` : `$${track.price.toFixed(2)}`}
                            </Button>
                          </>
                        )}
                        <ShareButtons url={`${window.location.origin}/tracks`} title={track.title} type="track" />
                        {track.lyrics && (
                          <Button onClick={() => setLyricsTrack(track)} variant="outline" className="rounded-none border-[#262626] hover:border-[#FF3B30] hover:text-[#FF3B30]" data-testid={`lyrics-track-${track.id}`}>
                            <FileText className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                      <CommentSection itemType="track" itemId={track.id} />
                      <TrackReviews trackId={track.id} trackTitle={track.title} />
                      <div className="mt-2 flex items-center gap-4">
                        <LikeButton itemType="track" itemId={track.id} />
                        <FireButton itemType="track" itemId={track.id} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      </div>

      {/* Purchase Dialog */}
      <Dialog open={purchaseDialogOpen} onOpenChange={setPurchaseDialogOpen}>
        <DialogContent className="bg-[#141414] border-[#262626] rounded-none max-w-md">
          <DialogHeader>
            <DialogTitle className="font-cabinet text-xl font-black uppercase">PURCHASE "{purchaseTrack?.title}"</DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            {purchaseTrack?.cover_image && (
              <div className="aspect-video bg-[#262626] mb-4 overflow-hidden">
                <img src={toFullUrl(purchaseTrack.cover_image)} alt={purchaseTrack.title} className="w-full h-full object-cover" />
              </div>
            )}
            <div className="bg-[#0A0A0A] border border-[#262626] p-4 mb-4">
              {purchaseTrack?.on_sale && purchaseTrack?.sale_price != null && (
                <div className="flex justify-between items-center mb-2">
                  <span className="text-[#A3A3A3]">Original Price</span>
                  <span className="font-mono line-through text-[#A3A3A3]">${purchaseTrack?.price?.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between items-center">
                <span className="text-[#A3A3A3]">{purchaseTrack?.on_sale && purchaseTrack?.sale_price != null ? 'Sale Price' : 'Price'}</span>
                <span className={`font-mono ${discountInfo ? 'line-through text-[#A3A3A3]' : purchaseTrack?.on_sale ? 'text-[#DAA520] font-bold' : 'text-white'}`}>
                  ${(purchaseTrack?.on_sale && purchaseTrack?.sale_price != null ? purchaseTrack.sale_price : purchaseTrack?.price)?.toFixed(2)}
                </span>
              </div>
              {discountInfo && (
                <>
                  <div className="flex justify-between items-center mt-2 text-[#22C55E]">
                    <span className="flex items-center gap-1"><Tag className="w-3 h-3" /> {discountInfo.code}</span>
                    <span className="font-mono">-${discountInfo.discount_amount.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center mt-2 pt-2 border-t border-[#262626]">
                    <span className="font-bold">Total</span>
                    <span className="font-mono text-xl text-[#FF3B30]">${discountInfo.final_price.toFixed(2)}</span>
                  </div>
                </>
              )}
            </div>
            <div className="space-y-4">
              <div>
                <Label className="overline">Email (for download link)</Label>
                <Input type="email" value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)} className="mt-2 bg-[#0A0A0A] border-[#262626] focus:border-[#FF3B30] rounded-none" placeholder="your@email.com" data-testid="purchase-email-input" />
              </div>
              <div>
                <Label className="overline">Discount Code (optional)</Label>
                <div className="flex gap-2 mt-2">
                  <Input value={discountCode} onChange={(e) => { setDiscountCode(e.target.value.toUpperCase()); setDiscountInfo(null); }} className="flex-1 bg-[#0A0A0A] border-[#262626] focus:border-[#FF3B30] rounded-none uppercase" placeholder="SAVE20" data-testid="discount-code-input" />
                  <Button type="button" variant="outline" onClick={validateDiscount} disabled={!discountCode || validatingDiscount} className="rounded-none border-[#262626] hover:border-[#FF3B30]" data-testid="validate-discount-btn">
                    {validatingDiscount ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply'}
                  </Button>
                </div>
              </div>
              <Button onClick={handleBuy} disabled={purchasing} className="w-full rounded-none h-12 font-bold text-sm" style={{ background: '#00D166', color: '#000', boxShadow: '0 2px 16px rgba(0,209,102,0.35)' }} data-testid="confirm-purchase-btn">
                {purchasing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> PROCESSING...</> : <><Download className="w-4 h-4 mr-2" /> PAY ${discountInfo ? discountInfo.final_price.toFixed(2) : (purchaseTrack?.on_sale && purchaseTrack?.sale_price != null ? purchaseTrack.sale_price : purchaseTrack?.price)?.toFixed(2)}</>}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Lyrics Modal */}
      <Dialog open={!!lyricsTrack} onOpenChange={(open) => { if (!open) setLyricsTrack(null); }}>
        <DialogContent className="bg-[#141414] border-[#262626] rounded-none max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-cabinet text-xl font-black uppercase" data-testid="lyrics-dialog-title">{lyricsTrack?.title}</DialogTitle>
            <p className="text-[#A3A3A3] text-sm">{lyricsTrack?.artist}</p>
          </DialogHeader>
          <div className="mt-4">
            <pre className="whitespace-pre-wrap font-sans text-[#E5E5E5] text-sm leading-relaxed" data-testid="lyrics-text">{lyricsTrack?.lyrics}</pre>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
